import { CUSTOM_ELEMENTS_SCHEMA, Component, ViewEncapsulation, input, output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgTemplateOutlet, NgClass, DecimalPipe } from '@angular/common';

import '@asn/badge';
import { ApIconComponent } from '@ap/icon';
import { ApTagComponent } from '@ap/tag';

import { ListItemActionType } from './list-item.model';
import { ApListItemAccordionComponent } from './list-item-accordion/list-item-accordion';
import { ApListItemButtonComponent } from './list-item-button/list-item-button';
import { ApListItemLinkComponent } from './list-item-link/list-item-link';
import { ApListItemSwitchComponent } from './list-item-switch/list-item-switch';

/** The list item component. */
@Component({
    selector: '[ap-list-item]',
    templateUrl: './list-item.component.html',
    styleUrls: ['./list-item.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ApListItemAccordionComponent,
        NgTemplateOutlet,
        ApListItemButtonComponent,
        ApListItemLinkComponent,
        ApListItemSwitchComponent,
        NgClass,
        ApIconComponent,
        ApTagComponent,
        DecimalPipe
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ApListItemComponent {
    /**
     * Required - The title of the `list item`.
     */
    public readonly title = input.required<string>();

    /**
     * set to true there should be a border at the bottom
     */
    public readonly showBottomBorder = input(false);

    /**
     * set to true there should be a border at the top
     */
    public readonly showTopBorder = input(false);

    /**
     * set to true to remove top padding when used as first list item
     */
    public readonly noTopPadding = input(false);

    /**
     * set to true to remove bottom padding when used as last list item
     */
    public readonly noBottomPadding = input(false);

    /**
     * optional icon used in front of the title
     */
    public readonly icon = input<string>();

    /**
     * the subTitle of the list item
     */
    public readonly subTitle = input<string>();

    /**
     * the tag of the list item
     */
    public readonly tagContent = input<string>();

    /**
     * the status of the tag
     */
    public readonly tagStatus = input<'error' | 'warning' | 'success'>();

    /**
     * the percentage of the list item
     */
    public readonly percentage = input<number>();

    /**
     * the value of the list item
     */
    public readonly value = input<string>();

    /**
     * makes the title bold; default = false
     */
    public readonly isTitleBold = input(false);

    /**
     * disabled list-item; default = false;
     */
    public readonly disabled = input(false);

    /**
     * `internalLink` show chevron right and mouseover
     * `externalLink` show external link icon and mouseover
     * `accordion` toggle show shevron up/down and mouseover. Custom content will be shown through content projection
     * `switch` show switch and mouseover
     * `action` only show mouseover
     * `none` (default)
     */
    public readonly actionType = input<ListItemActionType>('none');

    /**
     * Accordion opened by default? Default is `false`
     */
    public readonly accordionOpen = input(false);

    /**
     * Show a badge to the right of the title
     */
    public readonly badge = input<number>();

    /**
     * used to define a string that labels the current element. Use it in cases where a text label is not visible on the screen
     */
    public readonly ariaLabel = input<string>();

    /**
     * In combination with the actionType `switch` this will show the switch and hold the boolean
     */
    public readonly switchControl = input<null | FormControl<boolean>>();

    /**
     * Eventemitter that is emitted on click, keyboard enter and keyboard space
     */
    public readonly itemClicked = output<void>();

    protected get plusOrMinusPercentage(): string {
        const percentage = this.percentage();
        if (!percentage) {
            return '';
        }
        return percentage > 0 ? 'plus' : 'minus';
    }

    protected isLink(): boolean {
        const actionType = this.actionType();
        return actionType === 'internalLink' || actionType === 'externalLink';
    }

    protected isButton(): boolean {
        return this.actionType() === 'action';
    }

    protected isAccordion(): boolean {
        return this.actionType() === 'accordion';
    }

    protected isSwitch(): boolean {
        return this.actionType() === 'switch';
    }

    protected isDisabled(): boolean {
        return (this.actionType() === 'switch' && this.switchControl()?.disabled) || this.disabled();
    }

    protected isNone(): boolean {
        const actionType = this.actionType();
        return actionType === 'none' || !actionType;
    }

    protected hasAction(): boolean {
        const actionType = this.actionType();
        return (
            actionType === 'action' ||
            actionType === 'internalLink' ||
            actionType === 'externalLink' ||
            actionType === 'accordion' ||
            actionType === 'switch' ||
            (actionType && actionType !== 'none')
        );
    }

    protected onItemClicked(): void {
        // TODO: The 'emit' function requires a mandatory void argument
        this.itemClicked.emit();
    }
}
